import { useState, useEffect } from 'react';
import { SlidersHorizontal, ChevronDown, ChevronRight, CheckCircle, Save } from 'lucide-react';
import { useFirmSettings, useUpdateFirmSettings } from '../../hooks/useDemandLetters';
import { Button, Card } from '../ui';

// Must mirror SELECTABLE_WASTE_CATEGORIES in merchant.py (canonical order).
const SELECTABLE_WASTE_CATEGORIES = [
  'Gambling/Betting', 'Alcohol/Bars/Nightlife', 'Adult Entertainment',
  'Luxury/Designer', 'Jewelry/Watches', 'Dining', 'Food Delivery',
  'Travel', 'Shopping', 'Subscription', 'Fitness', 'Pet',
];
const DEFAULT_WASTE_CATEGORIES = [
  'Gambling/Betting', 'Alcohol/Bars/Nightlife', 'Adult Entertainment',
  'Luxury/Designer', 'Jewelry/Watches',
];

export default function WasteCategorySettings() {
  const { data: settings } = useFirmSettings();
  const updateMutation = useUpdateFirmSettings();

  const [expanded, setExpanded] = useState(false);
  const [wasteCategories, setWasteCategories] = useState<string[]>(DEFAULT_WASTE_CATEGORIES);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) {
      setWasteCategories(
        settings.waste_categories != null
          ? settings.waste_categories
          : DEFAULT_WASTE_CATEGORIES,
      );
    }
  }, [settings]);

  const toggleWasteCategory = (cat: string) => {
    setWasteCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat],
    );
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({ waste_categories: wasteCategories });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Card>
      {/* eslint-disable-next-line no-restricted-syntax -- full-width disclosure row: no ui primitive fits this layout */}
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="flex w-full items-center justify-between text-left"
      >
        <div className="flex items-start gap-2">
          <SlidersHorizontal size={16} className="mt-1 shrink-0 text-ink-faint" />
          <div>
            <h3 className="t-card text-ink-soft">Waste Categories</h3>
            <p className="mt-1 max-w-xl text-xs text-ink-faint">
              Choose which merchant categories should be flagged as potential marital waste
              (dissipation). Applies firm-wide the next time merchants are researched on a case.
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronDown size={16} className="shrink-0 text-ink-faint" />
        ) : (
          <ChevronRight size={16} className="shrink-0 text-ink-faint" />
        )}
      </button>

      {expanded && (
        <div className="mt-4">
          <div className="grid grid-cols-2 gap-2">
            {SELECTABLE_WASTE_CATEGORIES.map(cat => {
              const checked = wasteCategories.includes(cat);
              return (
                <label
                  key={cat}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                    checked
                      ? 'border-green-300 bg-green-100 text-green-800'
                      : 'border-cream-dark text-ink-soft hover:bg-cream'
                  }`}
                >
                  {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-green-700"
                    checked={checked}
                    onChange={() => toggleWasteCategory(cat)}
                  />
                  <span>{cat}</span>
                </label>
              );
            })}
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              size="lg"
              icon={saved ? CheckCircle : Save}
              loading={updateMutation.isPending}
              onClick={handleSave}
            >
              {saved ? 'Saved' : updateMutation.isPending ? 'Saving…' : 'Save Categories'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
